<template>
    <section name="course complete" class="bg-lightest col-span-12">
        <img src="/src/assets/backgrounds/confetti.png" alt="celebration" class="-mt-xl" />
        <div v-if="completedCourse" class="-mt-xl grid grid-cols-12">
            <div class="gap-md px-sm pb-xl col-span-12 flex flex-col lg:col-span-6 lg:col-start-4 lg:px-0">
                <h1 class="text-title-xl">Congratulations!</h1>
                <p>You have successfully completed:</p>

                <div class="gap-sm lg:gap-xl p-sm flex flex-col rounded-md bg-gradient-to-r from-(--surface-highlight) to-(--bg-primary)">
                    <p v-if="completedCourse.duration" class="text-">
                        {{ getFriendlyTimeFromSeconds(completedCourse?.duration) }}
                    </p>
                    <h2>{{ completedCourse.title }}</h2>
                    <p>{{ completedCourse.description }}</p>
                </div>
                <template v-if="!isVisitor(userCPRoles)">
                    <p>
                        We’re proud of your dedication and hard work. This achievement marks a significant step in your
                        journey.
                    </p>
                    <p class="mt-xl">Take a moment to celebrate—you’ve earned it!</p>
                </template>
                <template v-if="isVisitor(userCPRoles)">
                    <Button v-if="lmsStore.pendingCourses.length === 0" class="btn" @click="authStore.logout()">
                        <i class="i-arrow-white block" />
                        <span>Close the portal</span>
                    </Button>
                </template>
                <template v-else>
                    <Button v-if="lmsStore.pendingCourses.length === 0" class="btn" @click="lmsStore.handleLMSSwitch()">
                        <i class="i-arrow-white block" />
                        <span>Back to portal</span>
                    </Button>
                    <Button v-else class="btn" @click="lmsRouter.push({ name: 'about' })"> Start next course </Button>
                </template>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { ref } from 'vue';
import { onMounted } from 'vue';
import { useLMSStore } from '@/stores/lms';
import { useAuthStore } from '@/stores/auth';
import { getCPRolesFromToken, getFriendlyTimeFromSeconds, isVisitor } from '@/stores/helpers';
import lmsRouter from '@/router/lms-router';
import type { Course } from '@/stores/store-interfaces';

const completedCourse = ref<Course>();
const lmsStore = useLMSStore();
const authStore = useAuthStore();
const token = localStorage.getItem('token');
const userCPRoles = getCPRolesFromToken(token!);
const props = defineProps({
    courseId: {
        type: String,
        required: true,
    },
});

onMounted(async () => {
    await setup();
});

async function setup() {
    // remember to check for pending courses before allowing routing to portal

    if (lmsStore.pendingCourses.length > 0) {
        if (lmsStore.pendingCourses.some((course) => course.course_id === props.courseId)) {
            lmsRouter.replace({ name: 'course', params: { courseId: props.courseId } });
            return;
        } else {
            const testCourse = ref<Course>();
            try {
                testCourse.value = await lmsStore.getCourse(props.courseId);
            } catch {
                lmsRouter.replace({ name: 'about' });
                return;
            }
            completedCourse.value = testCourse.value;
        }
    }

    if (props.courseId === 'Visitor') {
        completedCourse.value = {
            course_id: 'Visitor',
            title: 'All Required Courses',
            description: 'You can now safely enter the DC',
            // duration: 0,
        } as Course;
        return;
    } else {
        completedCourse.value = await lmsStore.getCourse(props.courseId);
    }
}
</script>
